import { ThunkMiddleware } from 'redux-thunk';
import { VpcService, ProductService, KompisDefaultFields } from '@inter-ikea-kompis/services';
import { ConfigurationParser } from '../../util/ConfigurationParser';
import { Actions, RootState } from '..';
import {
	SET_GALLERY_VPC_CODE,
	SET_CLOSE_GALLERY,
	setPredefinedConfigurationId,
	setPersistedConfiguration,
} from '../sofaConfiguration';
import {
	setCurrentLightbox,
	setAppView,
	setLanguageChanged,
	setProductVariantsShowMore,
} from '../application/actions';
import { SET_CURRENT_LIGHTBOX, SET_IS_DEEPLINKED, SET_LOADING_MODELS } from '../application/types';
import { PredefinedConfiguration, VpcConfiguration } from 'sofa-reborn-react';
import { resetScreensaverTimeout } from '../screensaver/actions';
import ConfigurationThunk from '../application/thunk/ConfigurationThunk';
import { UrlService } from '../../services';
import { screenshotSuccess } from '../checkout/actions';
import { clearAdditionalCoverItems, setAdditionalCoverItems, setCoverOnly } from '../cover';
import { setZipCode } from '../localization/actions';
import { hidePopOver, showPopOver } from '../popOver';
import { SET_FAULTY_SECTIONS } from '../missing';

const configurationMiddleware: ThunkMiddleware<RootState, Actions> =
	(api) =>
	(next) =>
	async (action: Actions): Promise<Actions> => {
		const { dispatch } = api;
		const result = next(action);
		const state = api.getState();
		const configurationThunk = new ConfigurationThunk();

		switch (action.type) {
			case SET_LOADING_MODELS:
			case SET_GALLERY_VPC_CODE:
			case SET_CLOSE_GALLERY:
			case SET_IS_DEEPLINKED: {
				const {
					predefinedConfigurationId,
					persistedConfiguration,
					galleryPredefinedConfigurationId,
				} = api.getState().sofaConfiguration;
				const { appView, languageChanged, coverChanged, loadingCovers, productVariantsShowMore } =
					api.getState().application;
				const { missingCover, coverOnly } = api.getState().cover;
				const { settings, sofaRange, predefinedConfigurations } = api.getState().settings;
				const { platform, deviceType } = api.getState().deviceInfo;
				const { noSummaryPageBackButton, showSummary } = api.getState().checkout;

				/* SET_LOADING_MODELS = appInit, deeplink, reload, coverChanged in iOS */
				/* SET_GALLERY_VPC_CODE = product intro gallery or missing article lightbox */
				/* SET_IS_DEEPLINKED = After check if user is logged in */
				if (
					action.type === SET_GALLERY_VPC_CODE ||
					action.type === SET_LOADING_MODELS ||
					action.type === SET_IS_DEEPLINKED
				) {
					/* Close all lightboxes if summary page and cover not changed */
					if (appView === 'summary-page' && !coverChanged) {
						dispatch(setCurrentLightbox('none'));
					}

					let hasStartedConfiguration = false;
					let urlPredefinedConfigurationId: string | null = null;
					let isSPR = false;
					/* From product intro gallery or missing article lightbox */
					if (action.type === SET_GALLERY_VPC_CODE) {
						urlPredefinedConfigurationId = galleryPredefinedConfigurationId
							? galleryPredefinedConfigurationId
							: null;
					} else {
						isSPR = UrlService.isSPR();
						urlPredefinedConfigurationId = UrlService.getVpcOrSprId()
							? (UrlService.getVpcOrSprId() as string).toUpperCase()
							: null;
					}

					/* Check previous configuration if action.type = SET_LOADING_MODELS or SET_CLOSE_GALLERY */
					if (action.type === SET_LOADING_MODELS || SET_CLOSE_GALLERY) {
						const startedConfiguration = persistedConfiguration
							? JSON.parse(persistedConfiguration)
							: null;
						const storedPredefinedConfigurationId = predefinedConfigurationId
							? predefinedConfigurationId.toUpperCase()
							: null;

						if (urlPredefinedConfigurationId !== storedPredefinedConfigurationId) {
							dispatch(clearAdditionalCoverItems());
						}

						if (
							startedConfiguration &&
							!missingCover &&
							urlPredefinedConfigurationId === storedPredefinedConfigurationId
						) {
							if (!languageChanged && !missingCover && !coverChanged) {
								dispatch(setCurrentLightbox('none'));
							}

							if (languageChanged === 'series_gallery' || languageChanged === 'pig') {
								dispatch(setCurrentLightbox('product-intro-gallery'));
							}

							hasStartedConfiguration = true;
							if (loadingCovers === false) {
								/* Update redux persist with new predefined configuration id */
								dispatch(setPredefinedConfigurationId(urlPredefinedConfigurationId));
								dispatch(
									configurationThunk.set(
										startedConfiguration === 'EMPTY' ? null : startedConfiguration,
										startedConfiguration === 'EMPTY' ? true : false,
										urlPredefinedConfigurationId === storedPredefinedConfigurationId
									)
								);
							}
						}
					}

					/* Do not update the configuration if we have a started one */
					if (!hasStartedConfiguration) {
						if (urlPredefinedConfigurationId) {
							if (platform === 'kiosk') {
								dispatch(resetScreensaverTimeout());
							}
							if (urlPredefinedConfigurationId.toLowerCase() === 'blank') {
								dispatch(setCurrentLightbox('none'));
								dispatch(setPredefinedConfigurationId('blank'));
								dispatch(configurationThunk.set(null, true, false));
								/* Predefined configuration */
							} else if (urlPredefinedConfigurationId.length > 6) {
								/* Check if user deeplinks a cover item */
								const productService = new ProductService({
									locale: settings.locale,
									localizationPrefixApplication: settings.localizationPrefixApplication,
									localizationEndpoints: settings.localizationEndpoints,
								});
								const coverProduct = await productService.getProducts(
									[{ type: isSPR ? 'SPR' : 'ART', id: urlPredefinedConfigurationId }],
									KompisDefaultFields
								);
								const coverItem =
									coverProduct &&
									(coverProduct[0].content?.itemNoGlobal || coverProduct[0].content?.itemNoLocal)
										? predefinedConfigurations.find((configuration: PredefinedConfiguration) => {
												return (
													configuration.coverId ===
													(coverProduct[0].content?.itemNoGlobal ||
														coverProduct[0].content?.itemNoLocal)
												);
										  })
										: null;
								if (coverItem && coverItem.id) {
									dispatch(setCoverOnly(true));
									/* Update id to get the id for the configuration */
									urlPredefinedConfigurationId = coverItem.id;
								} else if (coverOnly) {
									dispatch(setCoverOnly(false));
								}
								const predefinedConfiguration = predefinedConfigurations.find(
									(configuration: PredefinedConfiguration) => {
										return (
											configuration.id === urlPredefinedConfigurationId ||
											configuration.localId === urlPredefinedConfigurationId
										);
									}
								);
								if (predefinedConfiguration) {
									if (!productVariantsShowMore) {
										dispatch(setCurrentLightbox('none'));
									} else {
										dispatch(setProductVariantsShowMore(false));
									}
								}
								dispatch(
									setPredefinedConfigurationId(
										coverItem && coverItem.coverId
											? coverItem.coverId
											: urlPredefinedConfigurationId
									)
								);
								dispatch(
									configurationThunk.set(
										predefinedConfiguration === undefined ? null : predefinedConfiguration,
										false,
										false
									)
								);
								/* VPC code configuration */
							} else {
								const vpcService = new VpcService({
									locale: settings.locale,
									localizationPrefixApplication: settings.localizationPrefixApplication,
									localizationEndpoints: settings.localizationEndpoints,
									applicationName: settings.applicationName,
									dexfApplicationId: settings.dexfApplicationId,
									dexfConfigurationVersion: settings.dexfConfigurationVersion,
								});

								vpcService
									.getConfiguration(urlPredefinedConfigurationId)
									.then(function (sofaData: VpcConfiguration): void {
										if (sofaData) {
											dispatch(
												setZipCode(
													sofaData.configuration.zipCode ? sofaData.configuration.zipCode : null,
													false
												)
											);

											if (sofaData.configuration.coverOnly) {
												dispatch(setCoverOnly(true));
											}
											/* Check if vpc code is for current range, if not show missing article */
											if (sofaData.application === sofaRange.toUpperCase()) {
												const parsedConfiguration = ConfigurationParser.parseConfiguration({
													configuration: sofaData.configuration['configuration'],
												});
												if ((noSummaryPageBackButton || showSummary) && sofaData.imageUrl) {
													/* Save image for configuration */
													dispatch(screenshotSuccess(sofaData.imageUrl[0].url));
												}
												dispatch(setCurrentLightbox('none'));
												dispatch(setPredefinedConfigurationId(urlPredefinedConfigurationId));
												dispatch(configurationThunk.set(parsedConfiguration, false, false));
												if (sofaData.configuration.additionalCoverItems) {
													dispatch(
														setAdditionalCoverItems(sofaData.configuration.additionalCoverItems)
													);
													if (sofaData.configuration.additionalCoverItems.length > 0) {
														dispatch(setAppView('summary-page'));
													}
												}
											} else {
												dispatch(setAppView('build-view'));
												dispatch(setCurrentLightbox('missing-article'));
												dispatch(configurationThunk.set(null, true, false));
											}
										}
									})
									.catch(() => {
										// No need to handle error, app is fine
									});
							}
						} else {
							/* Clear predefined configuration id from storage service */
							if (!coverChanged) {
								dispatch(setPersistedConfiguration(null));
							}
							if (deviceType === 'mobile' && coverChanged) {
								dispatch(setCurrentLightbox(coverChanged));
							} else if (!languageChanged && !coverChanged && !coverOnly) {
								dispatch(setCurrentLightbox('product-intro-gallery'));
							}
							/* Reset language changed */
							dispatch(setLanguageChanged(null));
						}
					}
				}
				break;
			}
			case SET_FAULTY_SECTIONS: {
				const standaloneSofabed = state.missing.standaloneSofabed[0];
				const missingCoverSection = state.missing.missingCoverSections[0];

				if (missingCoverSection && missingCoverSection.screenPosition) {
					const { screenPosition } = missingCoverSection;

					if (screenPosition.x === 0 || screenPosition.y === 0) {
						break;
					}

					dispatch(
						showPopOver({
							type: 'missing-cover-section',
							left: screenPosition.x,
							top: screenPosition.y,
						})
					);
				} else if (missingCoverSection === undefined) {
					dispatch(hidePopOver({ type: 'missing-cover-section' }));
				}

				if (standaloneSofabed && standaloneSofabed.screenPosition) {
					const { screenPosition } = standaloneSofabed;

					if (screenPosition.x === 0 || screenPosition.y === 0) {
						break;
					}

					dispatch(
						showPopOver({
							type: 'placement-information',
							left: screenPosition.x,
							top: screenPosition.y,
							translationKey: 'invalidSofabedOnSofa',
						})
					);
				}

				break;
			}
			case SET_CURRENT_LIGHTBOX: {
				if (action.value !== 'none' && state.popOver.placementInformation.showPopOver) {
					dispatch(hidePopOver({ type: 'placement-information' }));
				}
				break;
			}
		}
		return result;
	};

export { configurationMiddleware };
